import { create } from 'zustand';
import { Match } from '@/types/cricket';
import { 
  fetchMatches,
  createMatch,
  updateMatchScore,
  updateMatch as updateMatchAPI,
  deleteMatch as deleteMatchAPI,
  updateMatchStatus as updateMatchStatusAPI
} from '@/services/supabaseService';
import { toast } from '@/components/ui/use-toast';

interface MatchStore {
  matches: Match[];
  activeMatch: string | null;
  loading: boolean;
  error: string | null;
  
  // Actions
  fetchMatches: () => Promise<void>;
  createMatch: (match: Match) => Promise<void>;
  updateMatch: (match: Match) => Promise<void>;
  deleteMatch: (matchId: string) => Promise<void>;
  updateMatchStatus: (matchId: string, status: 'upcoming' | 'live' | 'completed') => Promise<void>;
  updateScore: (matchId: string, teamId: string, runs: number, wickets: number, overs: number, balls: number) => Promise<void>;
  setActiveMatch: (matchId: string | null) => void;
}

const useMatchStore = create<MatchStore>((set, get) => ({
  matches: [],
  activeMatch: null,
  loading: false,
  error: null,
  
  fetchMatches: async () => {
    set({ loading: true, error: null });
    try {
      const matchesData = await fetchMatches();
      set({ matches: matchesData, loading: false });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Error fetching matches:", error);
      set({ 
        error: message || "Failed to load matches", 
        loading: false 
      });
      toast({
        title: "Error",
        description: "Failed to load matches",
        variant: "destructive",
      });
    }
  },
  
  createMatch: async (match) => {
    try {
      const newMatch = await createMatch(match);
      set((state) => ({
        matches: [newMatch, ...state.matches]
      }));
      toast({
        title: "Success",
        description: "Match scheduled successfully!",
      });
      return newMatch;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Error creating match:", error);
      toast({
        title: "Error",
        description: message || "Failed to schedule match",
        variant: "destructive",
      });
      throw error;
    }
  },

  updateMatch: async (match) => {
    try {
      const updatedMatch = await updateMatchAPI(match);
      set((state) => ({
        matches: state.matches.map(m => 
          m.id === match.id ? { ...updatedMatch, ...match } : m
        )
      }));
      // Refresh data to ensure consistency
      await get().fetchMatches();
      toast({
        title: "Success",
        description: "Match updated successfully!",
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Error updating match:", error);
      toast({
        title: "Error",
        description: message || "Failed to update match",
        variant: "destructive",
      });
      throw error;
    }
  },

  deleteMatch: async (matchId) => {
    try {
      await deleteMatchAPI(matchId);
      set((state) => ({
        matches: state.matches.filter(m => m.id !== matchId)
      }));
      toast({
        title: "Success",
        description: "Match deleted successfully!",
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Error deleting match:", error);
      toast({
        title: "Error",
        description: message || "Failed to delete match",
        variant: "destructive",
      });
      throw error;
    }
  },

  updateMatchStatus: async (matchId, status) => {
    try {
      await updateMatchStatusAPI(matchId, status);
      set((state) => ({
        matches: state.matches.map(m => 
          m.id === matchId ? { ...m, status } : m
        )
      }));
      toast({
        title: "Success",
        description: `Match status updated to ${status}`,
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Error updating match status:", error);
      toast({
        title: "Error",
        description: message || "Failed to update match status",
        variant: "destructive",
      });
      throw error;
    }
  },

  updateScore: async (matchId, teamId, runs, wickets, overs, balls) => {
    try {
      await updateMatchScore(matchId, teamId, { runs, wickets, overs, balls });
      set((state) => ({
        matches: state.matches.map(match => {
          if (match.id === matchId) {
            if (teamId === match.team1Id) {
              return {
                ...match,
                team1Score: { runs, wickets, overs, balls }
              };
            } else {
              return {
                ...match,
                team2Score: { runs, wickets, overs, balls }
              };
            }
          }
          return match;
        })
      }));
      // Don't show toast for score updates as they happen frequently
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Error updating score:", error);
      toast({
        title: "Error",
        description: message || "Failed to update score",
        variant: "destructive",
      });
      throw error;
    }
  },

  setActiveMatch: (matchId) => set({ activeMatch: matchId }),
}));

export default useMatchStore; 